<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
    <div class="bg-card w-full max-w-md rounded-2xl shadow-2xl shadow-black border border-border overflow-hidden flex flex-col max-h-[90vh]">
      <div class="p-6 pb-2 border-b border-border flex-shrink-0">
        <h2 class="text-xl font-black text-foreground">{{ isEditing ? 'Modifier l\'abonnement' : 'Nouvel abonnement' }}</h2>
      </div>

      <div class="p-6 overflow-y-auto flex-grow custom-scrollbar">
        <form @submit.prevent="save" id="subForm" class="space-y-4">
          <div class="space-y-2">
            <label class="text-xs font-bold text-muted-foreground uppercase tracking-wider">Nom de l'abonnement</label>
            <Input v-model="form.name" required placeholder="Ex: Netflix, Salle de sport..." />
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div class="space-y-2">
              <label class="text-xs font-bold text-muted-foreground uppercase tracking-wider">Prix (€)</label>
              <Input type="number" step="0.01" v-model="form.price" required />
            </div>
            <div class="space-y-2">
              <label class="text-xs font-bold text-muted-foreground uppercase tracking-wider">Périodicité</label>
              <select v-model="form.period" class="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" required>
                <option value="monthly">Mensuel</option>
                <option value="yearly">Annuel</option>
              </select>
            </div>
          </div>

          <div class="space-y-2">
            <label class="text-xs font-bold text-muted-foreground uppercase tracking-wider">Date de début</label>
            <Input type="date" v-model="form.startDate" required />
          </div>

          <div class="space-y-2">
            <label class="text-xs font-bold text-muted-foreground uppercase tracking-wider">Prochain renouvellement</label>
            <Input type="date" v-model="form.renewalDate" required />
          </div>

          <div class="space-y-2 p-4 bg-muted/30 rounded-xl border border-border/50">
            <label class="text-xs font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
              <Bell class="w-3.5 h-3.5 text-amber-500" /> Paramètre d'alerte
            </label>
            
            <div class="space-y-3 mt-2">
              <select v-model="form.alertMode" class="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" required>
                <option value="none">Aucune alerte</option>
                <option value="offset">Délai avant renouvellement</option>
                <option value="periodic">Alerte périodique</option>
              </select>

              <div v-if="form.alertMode === 'offset'" class="space-y-2">
                <label class="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Me prévenir :</label>
                <select v-model="form.alertOffset" class="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                  <option value="0_day">Le jour même</option>
                  <option value="1_day">1 jour avant</option>
                  <option value="3_days">3 jours avant</option>
                  <option value="1_week">1 semaine avant</option>
                  <option value="2_weeks">2 semaines avant</option>
                  <option value="1_month">1 mois avant</option>
                  <option value="2_months">2 mois avant</option>
                </select>
                <p class="text-[10px] text-muted-foreground mt-1 italic text-right">L'alerte sera déclenchée le : {{ calculatedAlertDateFormatted }}</p>
              </div>
              
              <div v-if="form.alertMode === 'periodic'" class="space-y-3">
                <div class="space-y-1">
                  <label class="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Fréquence de l'alerte</label>
                  <select v-model="form.alertPeriod" class="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                    <option value="monthly">Tous les mois</option>
                    <option value="quarterly">Tous les 3 mois</option>
                    <option value="half-yearly">Tous les 6 mois</option>
                    <option value="yearly">Tous les ans</option>
                  </select>
                </div>
                <div class="space-y-1">
                  <label class="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Date de la 1ère alerte</label>
                  <Input type="date" v-model="form.alertDate" :required="form.alertMode === 'periodic'" />
                </div>
              </div>
            </div>
          </div>

          <div class="space-y-2">
            <label class="text-xs font-bold text-muted-foreground uppercase tracking-wider">Commentaire / Note</label>
            <textarea v-model="form.comment" rows="3" class="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" placeholder="Note optionnelle..."></textarea>
          </div>

        </form>
      </div>
      
      <div class="p-6 pt-4 border-t border-border flex justify-end gap-3 flex-shrink-0 bg-muted/10">
        <Button type="button" variant="outline" @click="$emit('close')" :disabled="saving">Annuler</Button>
        <Button type="submit" form="subForm" variant="finance" :disabled="saving">
          <RefreshCw v-if="saving" class="w-4 h-4 mr-2 animate-spin" />
          {{ isEditing ? 'Mettre à jour' : 'Ajouter' }}
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { Bell, RefreshCw } from 'lucide-vue-next'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { useGlobalState } from '@/composables/useGlobalState'
import { formatDateFr } from '@/lib/utils'

const props = defineProps({
  show: Boolean,
  subscription: Object
})
const emit = defineEmits(['close', 'saved'])
const { getAuthHeader } = useGlobalState()

const saving = ref(false)
const isEditing = computed(() => !!props.subscription)

const today = new Date().toISOString().split('T')[0]

const todayObj = new Date()
const nextMonthObj = new Date(todayObj)
nextMonthObj.setMonth(nextMonthObj.getMonth() + 1)

const form = ref({
  name: '',
  price: '',
  period: 'monthly',
  comment: '',
  startDate: today,
  renewalDate: nextMonthObj.toISOString().split('T')[0],
  alertDate: '',
  alertMode: 'offset',
  alertOffset: '1_week',
  alertPeriod: 'monthly'
})

onMounted(() => {
  if (props.subscription) {
    // Determine the alertMode for legacy data if missing
    let loadedMode = props.subscription.alertMode
    if (!loadedMode) {
        loadedMode = props.subscription.alertDate ? 'offset' : 'none'
    }
    
    form.value = { 
        ...props.subscription,
        alertMode: loadedMode,
        alertOffset: props.subscription.alertOffset || '1_week',
        alertPeriod: props.subscription.alertPeriod || 'monthly',
        comment: props.subscription.comment || ''
    }
  }
})

// Auto-calculate renewal date when creating or editing a subscription
watch([() => form.value.startDate, () => form.value.period], ([newStartDate, newPeriod]) => {
  if (isEditing.value) return
  if (!newStartDate) return
  const d = new Date(newStartDate)
  if (isNaN(d.getTime())) return
  
  const todayDate = new Date()
  todayDate.setHours(0, 0, 0, 0)
  
  do {
    if (newPeriod === 'monthly') {
      d.setMonth(d.getMonth() + 1)
    } else {
      d.setFullYear(d.getFullYear() + 1)
    }
  } while (d <= todayDate);

  form.value.renewalDate = d.toISOString().split('T')[0]
})

// Calculate alert date based on renewalDate and offset
const calculatedAlertDate = computed(() => {
  if (!form.value.renewalDate) return today
  
  const d = new Date(form.value.renewalDate)
  if (isNaN(d.getTime())) return today

  switch (form.value.alertOffset) {
    case '0_day': break;
    case '1_day': d.setDate(d.getDate() - 1); break;
    case '3_days': d.setDate(d.getDate() - 3); break;
    case '1_week': d.setDate(d.getDate() - 7); break;
    case '2_weeks': d.setDate(d.getDate() - 14); break;
    case '1_month': d.setMonth(d.getMonth() - 1); break;
    case '2_months': d.setMonth(d.getMonth() - 2); break;
  }
  
  return d.toISOString().split('T')[0]
})

const calculatedAlertDateFormatted = computed(() => formatDateFr(calculatedAlertDate.value))

const save = async () => {
  saving.value = true
  
  if (form.value.alertMode === 'offset') {
    form.value.alertDate = calculatedAlertDate.value
  } else if (form.value.alertMode === 'none') {
    form.value.alertDate = null
  }

  try {
    const method = isEditing.value ? 'PUT' : 'POST'
    const url = isEditing.value ? `/api/subscriptions/${props.subscription.id}` : '/api/subscriptions'
    
    const res = await fetch(url, {
      method,
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(form.value)
    })
    
    if (res.ok) {
      emit('saved')
    } else {
      const data = await res.json()
      alert("Erreur: " + JSON.stringify(data.errors))
    }
  } catch (e) {
    console.error(e)
  } finally {
    saving.value = false
  }
}
</script>
